import { useCallback } from 'react';
import { Maybe } from '@metamask/providers/dist/utils';
import { useDrfqService } from '@paradigm/features/src/unified-rfqs/contexts/drfqs';

import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import { Grfq } from '#/unified-rfqs/entities/grfq/domain';
import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import {
  useDrfqHistoryStore,
  useDrfqDraftsStore,
} from '#/unified-rfqs/contexts/drfqs';

import { exceptionLogger } from '@paradigm/logging';
import { isDrfqId } from '#/unified-rfqs/entities/rfq';
import { useRfqBuilderModalMethods } from '#/unified-rfqs/ui/rfq-builder/modal-context';
import { useUnifiedRfqsService } from '@paradigm/features/src/unified-rfqs/contexts/unified-rfqs';
import { useMarketPayloadCreator } from './shared';

type RfqId = Drfq['id'] | Grfq['id'];
export type CreateMarketFn = (args: {
  rfqId: RfqId | null;
  type: 'OB' | 'RFQ';
  isDraft?: Maybe<boolean>;
  prefillAccount?: Maybe<string>;
  onError: (msg: string) => void;
}) => void;

export default function useCreateMarket(isDraft?: Maybe<boolean>) {
  const marketBuilderModal = useRfqBuilderModalMethods();
  const createPayload = useMarketPayloadCreator();
  const drfqService = useDrfqService();
  const grfqService = useGrfqService();
  const { selectById } = useUnifiedRfqsService();
  const drfqHistoryStore = useDrfqHistoryStore();
  const drfqDraftsStore = useDrfqDraftsStore();

  const openMarketBuilder = useCallback(
    (
      rfq: Grfq | Drfq,
      type: 'OB' | 'RFQ',
      onError: (message: string) => void,
      prefillAccount?: Maybe<string>,
    ) => {
      try {
        const payload = createPayload(rfq, type, prefillAccount);
        marketBuilderModal.open(payload, onError);
      } catch {
        onError(`Failed to create a new ${type}.`);
      }
    },
    [createPayload, marketBuilderModal],
  );

  const selectRfq = useCallback(
    (rfqId: RfqId) => {
      if (isDrfqId(rfqId)) {
        const drfqHistory = isDraft
          ? drfqDraftsStore.selectById(rfqId)
          : drfqHistoryStore.selectById(rfqId);
        if (drfqHistory != null) return drfqHistory;
      }

      return selectById(rfqId);
    },
    [drfqHistoryStore, selectById, drfqDraftsStore, isDraft],
  );

  /**
   * Create market from an rfq in the store or fetch the rfq for a given id.
   */
  const createMarket: CreateMarketFn = useCallback(
    ({ rfqId, type, prefillAccount, onError }) => {
      if (rfqId == null) {
        onError(
          `Failed to create a new ${type}, base Market is not available.`,
        );
        return;
      }

      const storedRfq = selectRfq(rfqId);
      if (storedRfq != null) {
        openMarketBuilder(storedRfq, type, onError, prefillAccount);
        return;
      }

      const promise = isDrfqId(rfqId)
        ? drfqService.getDrfq({ rfqId })
        : grfqService.getRfq({ rfqId });

      promise
        .then((resp) => {
          if (resp.ok) {
            openMarketBuilder(resp.data, type, onError, prefillAccount);
            return;
          }

          const fetchType = isDrfqId(rfqId) ? 'RFQ' : 'OB';
          onError(
            `Failed to create a new ${type}, base ${fetchType} is not available.`,
          );
        })
        .catch(exceptionLogger('Unexpected failure on market creation.'));
    },
    [drfqService, grfqService, openMarketBuilder, selectRfq],
  );

  return { createMarket };
}
